import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Input, Select, Button } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { nextStages, stageLabel } from '../lib/appeal-stages';

/**
 * AppealAdvanceModal — advance an appeal to a later lifecycle stage.
 *
 * The stage picker offers the legal next stages (everything after the current
 * stage in the canonical order, plus the `withdrawn` side-exit). Date inputs
 * appear conditionally for the two stages that carry a date:
 *   - hearing_scheduled     → hearing_date
 *   - board_order_received  → board_order_received_date
 *
 * Submits POST /api/workflow/appeals/:appealId/advance. An illegal transition
 * returns 400 with an { error } body — surfaced verbatim via a toast.
 */

interface AdvanceBody {
  to_stage: string;
  note?: string | null;
  hearing_date?: string | null;
  board_order_received_date?: string | null;
}

export interface AppealAdvanceModalProps {
  appealId: string;
  currentStage: string;
  open: boolean;
  onClose: () => void;
  onAdvanced: () => void;
}

export function AppealAdvanceModal({
  appealId,
  currentStage,
  open,
  onClose,
  onAdvanced,
}: AppealAdvanceModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const options = useMemo(() => nextStages(currentStage), [currentStage]);
  const [toStage, setToStage] = useState<string>(options[0] ?? '');
  const [hearingDate, setHearingDate] = useState('');
  const [boardOrderReceivedDate, setBoardOrderReceivedDate] = useState('');
  const [note, setNote] = useState('');

  // Keep the selected stage valid whenever the option set changes (e.g. the
  // modal is reopened after a successful advance moved the appeal forward).
  const effectiveStage = options.includes(toStage) ? toStage : (options[0] ?? '');

  const mutation = useMutation<unknown, Error, void>({
    mutationFn: () => {
      const body: AdvanceBody = { to_stage: effectiveStage, note: note || null };
      if (effectiveStage === 'hearing_scheduled') body.hearing_date = hearingDate || null;
      if (effectiveStage === 'board_order_received') body.board_order_received_date = boardOrderReceivedDate || null;
      return api.post(`/api/workflow/appeals/${appealId}/advance`, body);
    },
    onSuccess: () => {
      toast({ variant: 'success', message: `Advanced to ${stageLabel(effectiveStage)}.` });
      queryClient.invalidateQueries({ queryKey: ['appeal', appealId] });
      queryClient.invalidateQueries({ queryKey: ['appeal-activity', appealId] });
      // Advancing the lifecycle stage changes which queue the appeal belongs to —
      // refresh the appeals pipeline (Dashboard worklist + AppealsQueues). Bare
      // prefix matches every ['appeals-queue', kind].
      queryClient.invalidateQueries({ queryKey: ['appeals-queue'] });
      onAdvanced();
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (effectiveStage) mutation.mutate();
  }

  const noOptions = options.length === 0;
  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 'var(--sp-1)',
    fontSize: 'var(--fs-sm)',
    fontWeight: 'var(--fw-medium)',
    color: 'var(--color-ink)',
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title="Advance appeal"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={noOptions || !effectiveStage || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Advance
          </Button>
        </>
      }
    >
      {noOptions ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
          This appeal is at a terminal stage ({stageLabel(currentStage)}). There is nothing to advance to.
        </p>
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{
            marginBottom: 'var(--sp-3)',
            fontSize: 'var(--fs-sm)',
            color: 'var(--color-ink-3)',
          }}>
            Current stage: <strong style={{ color: 'var(--color-ink)' }}>{stageLabel(currentStage)}</strong>
          </div>

          <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
            <span style={labelStyle}>Advance to</span>
            <Select
              value={effectiveStage}
              onChange={e => setToStage(e.target.value)}
              options={options.map(s => ({ value: s, label: stageLabel(s) }))}
            />
          </label>

          {effectiveStage === 'hearing_scheduled' && (
            <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
              <span style={labelStyle}>Hearing date</span>
              <Input
                type="date"
                value={hearingDate}
                onChange={e => setHearingDate(e.target.value)}
              />
            </label>
          )}

          {effectiveStage === 'board_order_received' && (
            <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
              <span style={labelStyle}>Board order received date</span>
              <Input
                type="date"
                value={boardOrderReceivedDate}
                onChange={e => setBoardOrderReceivedDate(e.target.value)}
              />
            </label>
          )}

          <label style={{ display: 'block' }}>
            <span style={labelStyle}>Note (optional)</span>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: 'var(--sp-2) var(--sp-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--r-md)',
                background: 'var(--color-card)',
                color: 'var(--color-ink)',
                fontSize: 'var(--fs-md)',
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
              }}
              placeholder="Context for the audit log"
            />
          </label>
        </form>
      )}
    </Modal>
  );
}
